import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Sparkles, Send, CalendarCheck, Users2, MessageSquare,
  HeartHandshake, ShieldCheck, Star, Flame, ListChecks, UserPlus, Coins,
  Award, IdCard,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import LevelBadge, { levelTier, TIER_ICONS } from "./LevelBadge.jsx";
import { useSmartBack } from "../utils/useSmartBack.js";
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import "./LevelsPage.css";

const API_URL = `${GREENWICH_BACKEND_URL}`;

const CATEGORIES = [
  { icon: Send, key: "apply" },
  { icon: CalendarCheck, key: "attend" },
  { icon: Users2, key: "community" },
  { icon: MessageSquare, key: "voice" },
  { icon: HeartHandshake, key: "mentorship" },
  { icon: ShieldCheck, key: "trust" },
  { icon: Star, key: "reviews" },
  { icon: Flame, key: "streak" },
  { icon: ListChecks, key: "tasks" },
  { icon: UserPlus, key: "referrals" },
];

const TIERS = ["bronze", "silver", "gold", "diamond", "legend"];

const CERT_STEPS = [
  { icon: CalendarCheck, key: "participate" },
  { icon: Award, key: "certificate" },
  { icon: IdCard, key: "bio" },
];

function LevelsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack('/feed');
  const { user } = useAuth();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!user) { setMe(null); return; }
    const token = localStorage.getItem("token");
    fetch(`${API_URL}/api/gamification/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => { if (data.success) setMe(data); })
      .catch(() => {});
  }, [user]);

  const level = me?.level || 1;
  const myTier = levelTier(level);
  const progressPct = me
    ? me.maxLevel ? 100 : Math.min(100, Math.max(4, Math.round((me.xpIntoLevel / Math.max(1, me.xpForNextLevel)) * 100)))
    : 0;

  return (
    <div className="lvp-page">
      <div className="lvp-shell">
        <div className="lvp-hero">
          <div className="lvp-hero-icon"><Sparkles size={26} strokeWidth={2} /></div>
          <h1 className="lvp-hero-title">{t("pages.levels.title")}</h1>
          <p className="lvp-hero-sub">{t("pages.levels.subtitle")}</p>
        </div>

        {user && me && (
          <div className={`lvp-progress-card tier-${myTier}`}>
            <div className="lvp-progress-top">
              <LevelBadge level={level} size="lg" clickable={false} showLabel />
              {!me.maxLevel && <span className="lvp-progress-next">{t("pages.levels.nextLevel", { level: level + 1 })}</span>}
              {me.maxLevel && <span className="lvp-progress-next">{t("pages.levels.maxLevelReached")}</span>}
            </div>
            <div className="lvp-progress-track">
              <div className="lvp-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link to="/shop" className="lvp-greencoins-pill">
                <Coins size={14} /> {me.greencoins || 0} {t("pages.levels.greencoins")}
              </Link>
              <Link to="/tasks" className="lvp-greencoins-pill">
                <ListChecks size={14} /> {t("pages.levels.tasksLink")}
              </Link>
            </div>
          </div>
        )}

        <section className="lvp-section">
          <h2 className="lvp-section-title">{t("pages.levels.howTitle")}</h2>
          <p className="lvp-section-sub">{t("pages.levels.howSub")}</p>
          <div className="lvp-cat-grid">
            {CATEGORIES.map(({ icon: Icon, key }) => (
              <div className="lvp-cat-card" key={key}>
                <div className="lvp-cat-icon"><Icon size={19} strokeWidth={2} /></div>
                <h3>{t(`pages.levels.categories.${key}.title`)}</h3>
                <p>{t(`pages.levels.categories.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="lvp-section">
          <h2 className="lvp-section-title">{t("pages.levels.certTitle")}</h2>
          <p className="lvp-section-sub">{t("pages.levels.certSub")}</p>
          <div className="lvp-cat-grid">
            {CERT_STEPS.map(({ icon: Icon, key }) => (
              <div className="lvp-cat-card" key={key}>
                <div className="lvp-cat-icon"><Icon size={19} strokeWidth={2} /></div>
                <h3>{t(`pages.levels.certSteps.${key}.title`)}</h3>
                <p>{t(`pages.levels.certSteps.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="lvp-section">
          <h2 className="lvp-section-title">{t("pages.levels.tiersTitle")}</h2>
          <p className="lvp-section-sub">{t("pages.levels.tiersSub")}</p>
          <div className="lvp-tier-ladder">
            {TIERS.map((tier) => {
              const TierIcon = TIER_ICONS[tier];
              return (
                <div className={`lvp-tier-row tier-${tier}${tier === myTier ? " current" : ""}`} key={tier}>
                  <span className="lvp-tier-icon"><TierIcon size={17} strokeWidth={2.2} /></span>
                  <div className="lvp-tier-copy">
                    <h4>{t(`pages.levels.tierNames.${tier}`)}</h4>
                    <p>{t(`pages.levels.tierDescs.${tier}`)}</p>
                  </div>
                  {tier === myTier && <span className="lvp-tier-you">{t("pages.levels.youAreHere")}</span>}
                </div>
              );
            })}
          </div>
        </section>

        <button className="lvp-back-btn" onClick={goBack}>
          <ArrowLeft size={16} /> {t("pages.levels.back")}
        </button>
      </div>
    </div>
  );
}

export default LevelsPage;
